import { useCallback, useEffect, useState } from 'react';
import { salesApi, PlatformApiError } from '../lib/platformApi';

/**
 * ═══ EVERYTHING AFTER "WON" ═══
 *
 * ⚠ TWO LISTS, NOT A CHART. "Who owes me money" and "who decides again soon" are the only two
 * revenue questions worth answering at this size, and both are lists you act on. A revenue graph is
 * something you look at.
 *
 * ⚠ AND NOTHING HERE SWITCHES A SCHOOL OFF. The overdue list shouts; a person decides. In a business
 * where every principal knows every other principal, dunning automation is the risk, not the saving.
 */

interface Owed {
  accountId: string; name: string; number: string; amountPaise: number;
  dueOn: string; daysOverdue: number;
}
interface Renewal {
  accountId: string; name: string; renewsOn: string; noticeBy: string;
  daysToRenewal: number; pricePaise: number; why: string;
}
interface BoardT {
  outstandingPaise: number; overduePaise: number; overdueCount: number;
  bookedThisYearPaise: number; overdue: Owed[]; renewals: Renewal[]; notes: string[];
}

interface Grant {
  id: string; module: string; grantedOn: string; becauseWhoAsked: string | null;
  note: string | null; revokedOn: string | null; revokedReason: string | null; isLive: boolean;
}
interface Step { key: string; label: string; order: number; doneOn: string | null; note: string | null; isDone: boolean }
interface Sub {
  id: string; planName: string; basis: string; basisLabel: string; period: string; periodLabel: string;
  pricePaise: number; startsOn: string; renewsOn: string; noticeDays: number; noticeBy: string;
  daysToRenewal: number; state: string; stateReason: string | null;
}
interface Inv {
  id: string; number: string; state: string; description: string; amountPaise: number;
  studentCountAtRaise: number | null; issuedOn: string; dueOn: string; paidOn: string | null;
  paidHow: string | null; cancelledReason: string | null; isOverdue: boolean; daysOverdue: number;
}
interface Promise_ { id: string; body: string; saidBy: string | null; raisedOn: string; isStalePromise: boolean }
interface Account {
  id: string; prospectId: string; tenantId: string; contractingName: string;
  ownerName: string | null; ownerRole: string | null; gstin: string | null; signedOn: string;
  onboardingRemaining: number; isOnboarded: boolean;
  grants: Grant[]; onboarding: Step[]; subscription: Sub | null; invoices: Inv[];
  openPromises: Promise_[];
}

/** ₹1,80,000 — grouped the Indian way, because ₹180,000 reads as wrong to everyone who sees it. */
function rupees(paise: number): string {
  const r = Math.round(paise / 100);
  const s = String(r);
  if (s.length <= 3) return `₹${s}`;
  const last3 = s.slice(-3);
  let rest = s.slice(0, -3);
  const groups: string[] = [];
  while (rest.length > 2) { groups.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
  if (rest) groups.unshift(rest);
  return `₹${groups.join(',')},${last3}`;
}

export function AccountsPanel() {
  const [board, setBoard] = useState<BoardT | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [b, list] = await Promise.all([
        salesApi.get<BoardT>('/api/platform/sales/accounts/board'),
        salesApi.get<Account[]>('/api/platform/sales/accounts'),
      ]);
      setBoard(b && Array.isArray(b.overdue) ? b : null);
      setAccounts(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e instanceof PlatformApiError ? e.message : 'Could not load the accounts.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div>
      {error && <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: '0 0 10px' }}>{error}</p>}

      {board && (
        <>
          <div className="card" style={{ padding: 18, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: 10 }}>
              <Stat n={rupees(board.overduePaise)} label={`overdue · ${board.overdueCount}`} />
              <Stat n={rupees(board.outstandingPaise)} label="outstanding" />
              {/* ⚠ The Indian financial year, matching the invoice numbering. Anything else reports
                  a figure that disagrees with the invoices it is made of. */}
              <Stat n={rupees(board.bookedThisYearPaise)} label="booked this financial year" />
            </div>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {board.notes.map((n, i) => (
                <li key={i} style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 3 }}>{n}</li>
              ))}
            </ul>
          </div>

          {board.overdue.length > 0 && (
            <div className="card" style={{ padding: 18, marginBottom: 16, borderLeft: '3px solid var(--danger)' }}>
              <div style={{ fontWeight: 650, marginBottom: 2 }}>Owed to you</div>
              {/* ⚠ Oldest first. Sorting by amount would put the biggest recent invoice above the
                  one quietly ignored since October. */}
              <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 10px' }}>
                Oldest first — a ninety-day debt is a different conversation from a five-day one.
              </p>
              {board.overdue.map((o) => (
                <div key={o.number} style={{
                  display: 'flex', gap: 10, alignItems: 'baseline',
                  borderTop: '1px solid var(--line-2)', padding: '8px 0',
                }}>
                  <span style={{ fontWeight: 600, fontSize: 13.5 }}>{o.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{o.number}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 13 }}>{rupees(o.amountPaise)}</span>
                  <span style={{ fontSize: 12, color: 'var(--danger)', minWidth: 90, textAlign: 'right' }}>
                    {o.daysOverdue}d overdue
                  </span>
                </div>
              ))}
            </div>
          )}

          {board.renewals.length > 0 && (
            <div className="card" style={{ padding: 18, marginBottom: 16 }}>
              <div style={{ fontWeight: 650, marginBottom: 2 }}>Deciding again soon</div>
              {/* ⚠ THE NOTICE DATE IS THE DEADLINE, not the renewal. A ninety-day notice period
                  means the conversation happens three months before the date on the contract. */}
              <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '0 0 10px' }}>
                By the notice date, not the renewal date — that is when the conversation has to happen.
              </p>
              {board.renewals.map((r) => (
                <div key={r.accountId} style={{
                  display: 'flex', gap: 10, alignItems: 'baseline',
                  borderTop: '1px solid var(--line-2)', padding: '8px 0', flexWrap: 'wrap',
                }}>
                  <span style={{ fontWeight: 600, fontSize: 13.5 }}>{r.name}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-2)' }}>{r.why}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {accounts.length === 0
        ? <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
            No accounts yet. A deal becomes one when it is won and provisioned.
          </div>
        : accounts.map((a) => (
          <div key={a.id} className="card" style={{ padding: 14, marginBottom: 8 }}>
            <button onClick={() => setOpenId(openId === a.id ? null : a.id)}
              style={{
                display: 'flex', gap: 10, alignItems: 'baseline', width: '100%', textAlign: 'left',
                background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit',
              }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{a.contractingName}</span>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {a.subscription
                  ? `${a.subscription.planName} · ${rupees(a.subscription.pricePaise)} ${a.subscription.basisLabel} · ${a.subscription.periodLabel}`
                  : 'no subscription recorded'}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: a.isOnboarded ? 'var(--ink-3)' : 'var(--warn)' }}>
                {a.isOnboarded ? 'onboarded' : `${a.onboardingRemaining} onboarding steps left`}
              </span>
            </button>

            {openId === a.id && <AccountDetail a={a} onChanged={() => void load()} />}
          </div>
        ))}
    </div>
  );
}

function AccountDetail({ a, onChanged }: { a: Account; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);

  const complete = async (key: string) => {
    setBusy(true);
    try { await salesApi.post(`/api/platform/sales/accounts/${a.id}/onboarding`, { key, note: null }); onChanged(); }
    catch { /* reloads either way */ } finally { setBusy(false); }
  };

  const markPaid = async (invoiceId: string) => {
    setBusy(true);
    try { await salesApi.post(`/api/platform/sales/accounts/${a.id}/invoices/${invoiceId}/paid`, { on: null, how: null }); onChanged(); }
    catch { /* reloads either way */ } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      {a.subscription && (
        <p style={{ fontSize: 12.5, color: 'var(--ink-2)', margin: '0 0 10px' }}>
          Signed {a.signedOn}{a.ownerName ? ` with ${a.ownerName}${a.ownerRole ? `, ${a.ownerRole}` : ''}` : ''}.
          {' '}Renews {a.subscription.renewsOn} — <strong>notice by {a.subscription.noticeBy}</strong>.
          {a.subscription.state !== 'Active' ? ` ${a.subscription.state}: ${a.subscription.stateReason}` : ''}
        </p>
      )}

      {/* ⚠ CARRIED ACROSS FROM THE DEAL. Nothing is more demoralising than a signed customer whose
          pre-sale promises are stranded in a "closed" list — and these are exactly what somebody
          means in month eighteen by "we were told this was included". */}
      {a.openPromises.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 4 }}>Still owed from the sale</div>
          {a.openPromises.map((p) => (
            <div key={p.id} style={{ fontSize: 12.5, color: p.isStalePromise ? 'var(--danger)' : 'var(--ink-2)' }}>
              {p.body}{p.saidBy ? ` — ${p.saidBy}` : ''} ({p.raisedOn})
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 4 }}>Onboarding</div>
        {a.onboarding.map((s) => (
          <div key={s.key} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 3 }}>
            <span style={{ fontSize: 12.5, textDecoration: s.isDone ? 'line-through' : 'none' }}>{s.label}</span>
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{s.doneOn ?? ''}</span>
            {!s.isDone && (
              <button className="btn" disabled={busy} style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11 }}
                      onClick={() => void complete(s.key)}>Done</button>
            )}
          </div>
        ))}
      </div>

      {a.grants.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 4 }}>What they were given</div>
          {a.grants.map((g) => (
            <div key={g.id} style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 2 }}>
              {g.module} · {g.grantedOn}
              {/* ⚠ The clause nobody builds and everybody needs eighteen months later. */}
              {g.becauseWhoAsked ? ` · asked for by ${g.becauseWhoAsked}` : ''}
              {g.revokedOn ? ` · switched off ${g.revokedOn} (${g.revokedReason})` : ''}
            </div>
          ))}
        </div>
      )}

      {a.invoices.length > 0 && (
        <div>
          <div className="eyebrow" style={{ fontSize: 9.5, marginBottom: 4 }}>Invoices</div>
          {a.invoices.map((i) => (
            <div key={i.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 3 }}>
              <span style={{ fontSize: 12, color: 'var(--ink-3)', minWidth: 118 }}>{i.number}</span>
              <span style={{ fontSize: 12.5 }}>{i.description}</span>
              <span style={{ marginLeft: 'auto', fontSize: 12.5 }}>{rupees(i.amountPaise)}</span>
              <span style={{ fontSize: 12, minWidth: 96, textAlign: 'right',
                             color: i.isOverdue ? 'var(--danger)' : 'var(--ink-3)' }}>
                {i.state === 'Paid' ? `paid ${i.paidOn}` :
                 i.state === 'Cancelled' ? 'cancelled' :
                 i.isOverdue ? `${i.daysOverdue}d overdue` : `due ${i.dueOn}`}
              </span>
              {/* ⚠ THE SLOT IS ALWAYS THERE, EVEN WHEN THE BUTTON IS NOT. Without it the paid rows
                  and the unpaid ones use different widths and the amounts stop lining up — which on
                  a column of money reads as a rendering fault. */}
              <span style={{ minWidth: 52, textAlign: 'right' }}>
                {i.state !== 'Paid' && i.state !== 'Cancelled' && (
                  <button className="btn" disabled={busy} style={{ padding: '2px 8px', fontSize: 11 }}
                          onClick={() => void markPaid(i.id)}>Paid</button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 650 }}>{n}</div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{label}</div>
    </div>
  );
}
